import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import Layout from '../component/layout';
import { useFacilityServiceRequests } from './hooks/use_facility_service_requests';
import { getItem } from '../../../utils/localStorage';
import { makeRequest2 } from '../../../utils/makeRequest';
import { voidInvoice } from '../../../utils/urls';
import { toastify } from '../../../utils/toast';
import { useFacilityStore } from '../../../app/z-store/store';

function VasInvoices() {
    const [searchKeyword, setSearchKeyword] = useState("");
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const navigate = useNavigate();
    const toast = useRef(null);
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);

    // Destructuring to match the hook's structure
    const {
        states: { loading, invoices, error },
        handlers: { downloadInvoicePDF, fetchInvoices }
    } = useFacilityServiceRequests();

    useEffect(() => {
        const loadInvoices = async () => {
            try {
                console.log('Loading VAS invoices...');
                await fetchInvoices();
            } catch (error) {
                console.error('Error in loadInvoices:', error);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message || 'Failed to fetch invoices'
                });
            }
        };

        loadInvoices();
    }, []);

    useEffect(() => {
        console.log('Invoices state updated:', invoices);
    }, [invoices]);

    const statusTemplate = (rowData) => {
        const status = rowData.status?.toLowerCase() || '';
        let color, bgColor;

        // Match styling with lease/levy invoices
        if (status === "paid") {
            color = "#4caf50";
            bgColor = "#e8f5e9";
        } else if (status === "pending" || status === "partially paid") {
            color = "#ff9800";
            bgColor = "#fff3e0";
        } else if (status === "overdue") {
            color = "#f44336";
            bgColor = "#ffebee";
        } else if (status === "cancelled" || status === "void") {
            color = "#9e9e9e";
            bgColor = "#f5f5f5";
        } else {
            color = "#2196f3";
            bgColor = "#e3f2fd";
        }

        return (
            <span
                style={{
                    fontWeight: "bold",
                    color: color,
                    padding: "5px 10px",
                    borderRadius: "15px",
                    backgroundColor: bgColor,
                }}
            >
                {rowData.status}
            </span>
        );
    };

    const actionTemplate = (rowData) => {
        const handleDownload = async (e) => {
            e.stopPropagation();
            try {
                await downloadInvoicePDF('printable-invoice', rowData);
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Invoice downloaded successfully!'
                });
            } catch (error) {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to download invoice'
                });
            }
        };

        const handleVoidInvoice = async (e, invoiceId) => {
            e.stopPropagation();
            confirmDialog({
                message: "Are you sure you want to void this invoice?",
                header: "Void Confirmation",
                icon: "pi pi-exclamation-triangle",
                acceptClassName: "p-button-warning",
                accept: async () => {
                    try {
                        const payload = {
                            reason: "Voided by property manager",
                            userId: await getItem('selectedCustomerId'),
                            userName: "System Admin",  // Replace with actual user name
                            userRole: "Property Manager",  // Replace with actual role
                        };

                        const response = await makeRequest2(
                            `${voidInvoice}/${selectedFacilityId}/${invoiceId}`,
                            "POST",
                            payload
                        );

                        if (response.data) {
                            toastify("Invoice successfully voided", "success");
                            fetchInvoices(); // Refresh the invoices list
                        } else {
                            toastify(response.error || "Failed to void invoice", "error");
                        }
                    } catch (error) {
                        toast.current?.show({
                            severity: "error",
                            summary: "Error",
                            detail: error.message || "An error occurred while voiding the invoice",
                        });
                    }
                },
            });
        };

        return (
            <div className="flex gap-2">
                <Button
                    icon="ti ti-eye"
                    className="p-button-rounded p-button-success"
                    onClick={() => navigate(`/facility/value_added_services/invoice/${rowData._id}`)}
                    tooltip="View Details"
                />
                {(rowData.status?.toLowerCase() !== "cancelled" &&
                    rowData.status?.toLowerCase() !== "paid" &&
                    rowData.status?.toLowerCase() !== "void") && (
                        <Button
                            icon="ti ti-trash"
                            className="p-button-rounded p-button-warning"
                            tooltip="Void Invoice"
                            onClick={(e) => handleVoidInvoice(e, rowData._id)}
                        />
                    )}
                {/* <Button
                    icon="ti ti-download"
                    className="p-button-rounded p-button-primary"
                    onClick={handleDownload}
                    tooltip="Download Invoice"
                /> */}
            </div>
        );
    };

    const header = (
        <div className="flex align-items-center">
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText
                    placeholder="Search invoices..."
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    style={{ height: "50px" }}
                />
            </span>
        </div>
    );

    const filterInvoices = (invoices) => {
        return (invoices || []).filter((invoice) => {
            if (!searchKeyword.trim()) return true;

            const searchFields = [
                invoice.customerName,
                invoice.serviceName,
                invoice.invoiceNumber,
                invoice.accountNumber,
                invoice.status,
                invoice.formattedAmount,
                invoice.formattedDate
            ];

            const searchString = searchFields.filter(Boolean).join(' ').toLowerCase();
            return searchString.includes(searchKeyword.toLowerCase());
        });
    };

    // Filter invoices by status
    const filteredInvoices = filterInvoices(invoices);
    const allInvoices = filteredInvoices;
    const pendingInvoices = filteredInvoices.filter(invoice =>
        invoice.status?.toLowerCase() === "pending");
    const paidInvoices = filteredInvoices.filter(invoice =>
        invoice.status?.toLowerCase() === "paid");
    const overdueInvoices = filteredInvoices.filter(invoice =>
        invoice.status?.toLowerCase() === "overdue");
    const cancelledInvoices = filteredInvoices.filter(invoice =>
        invoice.status?.toLowerCase() === "cancelled" ||
        invoice.status?.toLowerCase() === "void");

    // Get appropriate invoices based on active tab
    const getInvoicesByTab = (tabIndex) => {
        switch (tabIndex) {
            case 0: return allInvoices;
            case 1: return pendingInvoices;
            case 2: return paidInvoices;
            case 3: return overdueInvoices;
            case 4: return cancelledInvoices;
            default: return allInvoices;
        }
    };

    const handleTabClick = (index) => {
        setActiveTabIndex(index);
    };

    return (
        <Layout>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/app/">Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">VAS Management</li>
                                <li className="breadcrumb-item">Invoices</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Service Invoices</h5>
                            </div>
                            <div className="card-body">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <ul className="nav nav-tabs invoice-tab border-bottom" id="invoiceTab" role="tablist">
                                                <li className="nav-item" role="presentation">
                                                    <button
                                                        className={`nav-link ${activeTabIndex === 0 ? 'active' : ''}`}
                                                        onClick={() => handleTabClick(0)}
                                                        type="button"
                                                        role="tab"
                                                    >
                                                        <span className="d-flex align-items-center gap-2">
                                                            All <span className="avtar rounded-circle bg-light-primary">{allInvoices.length}</span>
                                                        </span>
                                                    </button>
                                                </li>
                                                <li className="nav-item" role="presentation">
                                                    <button
                                                        className={`nav-link ${activeTabIndex === 1 ? 'active' : ''}`}
                                                        onClick={() => handleTabClick(1)}
                                                        type="button"
                                                        role="tab"
                                                    >
                                                        <span className="d-flex align-items-center gap-2">
                                                            Pending <span className="avtar rounded-circle bg-light-warning">{pendingInvoices.length}</span>
                                                        </span>
                                                    </button>
                                                </li>
                                                <li className="nav-item" role="presentation">
                                                    <button
                                                        className={`nav-link ${activeTabIndex === 2 ? 'active' : ''}`}
                                                        onClick={() => handleTabClick(2)}
                                                        type="button"
                                                        role="tab"
                                                    >
                                                        <span className="d-flex align-items-center gap-2">
                                                            Paid <span className="avtar rounded-circle bg-light-success">{paidInvoices.length}</span>
                                                        </span>
                                                    </button>
                                                </li>
                                                <li className="nav-item" role="presentation">
                                                    <button
                                                        className={`nav-link ${activeTabIndex === 3 ? 'active' : ''}`}
                                                        onClick={() => handleTabClick(3)}
                                                        type="button"
                                                        role="tab"
                                                    >
                                                        <span className="d-flex align-items-center gap-2">
                                                            Overdue <span className="avtar rounded-circle bg-light-danger">{overdueInvoices.length}</span>
                                                        </span>
                                                    </button>
                                                </li>
                                                <li className="nav-item" role="presentation">
                                                    <button
                                                        className={`nav-link ${activeTabIndex === 4 ? 'active' : ''}`}
                                                        onClick={() => handleTabClick(4)}
                                                        type="button"
                                                        role="tab"
                                                    >
                                                        <span className="d-flex align-items-center gap-2">
                                                            Cancelled <span className="avtar rounded-circle bg-light-secondary">{cancelledInvoices.length}</span>
                                                        </span>
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="row py-2 mb-2">
                                            {header}
                                        </div>
                                        <DataTable
                                            value={getInvoicesByTab(activeTabIndex)}
                                            loading={loading}
                                            emptyMessage="No invoices found."
                                            paginator
                                            rows={5}
                                            stripedRows
                                            tableStyle={{ minWidth: "50rem" }}
                                            scrollable
                                            responsiveLayout="scroll"
                                        >
                                            <Column
                                                field="customerName"
                                                header="Customer"
                                            />
                                            <Column
                                                field="serviceName"
                                                header="Service"
                                            />
                                            <Column
                                                field="formattedAmount"
                                                header="Amount"
                                                align="right"
                                            />
                                            <Column
                                                field="formattedDate"
                                                header="Date"
                                            />
                                            <Column
                                                field="status"
                                                header="Status"
                                                body={statusTemplate}
                                            />
                                            <Column
                                                body={actionTemplate}
                                                header="Actions"
                                                style={{ width: '15%' }}
                                            />
                                        </DataTable>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default VasInvoices;